import React from 'react';
import { CheckCircle2, Clock, X } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from './ImageUpload';

/**
 * Feb 2026 — AttendanceDialog
 *
 * A one-shot modal that fires on the FIRST password login of the
 * Muscat day for any sub-admin with a configured duty window. The
 * backend (`services/attendance.py`) decides eligibility and hands
 * us a fully-computed payload — this component is purely presentation.
 *
 * Shape of `attendance`:
 *
 *   {
 *     show, name, email, on_time,
 *     minutes_late, duty_start, first_login_display,
 *     monthly_late_minutes, monthly_late_hours, monthly_late_remainder,
 *     month_label,
 *   }
 */
const formatDuration = (hours, remainderMin) => {
  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  parts.push(`${remainderMin} minute${remainderMin === 1 ? '' : 's'}`);
  return parts.join(' and ');
};

const AttendanceDialog = () => {
  const { attendance, dismissAttendance } = useAdminAuth();
  const { settings } = useSettings();
  const logoUrl = resolveImageUrl(settings?.logo_image || '');

  if (!attendance || !attendance.show) return null;

  const {
    name,
    on_time: onTime,
    minutes_late: minutesLate,
    duty_start: dutyStart,
    first_login_display: firstLoginDisplay,
    monthly_late_hours: monthlyHours,
    monthly_late_remainder: monthlyRemainder,
    monthly_late_minutes: monthlyTotalMinutes,
    month_label: monthLabel,
  } = attendance;

  const displayName = (name || '').trim() || 'Team member';
  const accent = onTime ? '#059669' : '#B91C1C';   // green-600 / red-700
  const accentBg = onTime ? '#ECFDF5' : '#FEF2F2'; // green-50 / red-50
  const accentBorder = onTime ? '#A7F3D0' : '#FECACA';

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attendance-title"
      data-testid="attendance-dialog"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header — branded band with logo */}
        <div className="relative bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0078D4] px-6 pt-6 pb-8">
          <button
            type="button"
            onClick={dismissAttendance}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label="Close"
            data-testid="attendance-close"
          >
            <X className="w-4 h-4" />
          </button>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              className="h-10 w-auto max-w-[180px] object-contain mb-3"
              data-testid="attendance-logo"
            />
          ) : (
            <div className="h-10 w-32 rounded bg-white/10 mb-3" aria-hidden />
          )}
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-bold">
            Daily attendance
          </div>
          <div className="text-white text-lg font-extrabold mt-1 leading-snug">
            {onTime ? 'You are present on time.' : `You are ${minutesLate} minute${minutesLate === 1 ? '' : 's'} late today.`}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p
            id="attendance-title"
            className="text-[15px] leading-relaxed text-slate-800"
            data-testid="attendance-message"
          >
            Dear <span className="font-bold text-slate-900">{displayName}</span>,{' '}
            {onTime ? (
              <>you are <span className="font-bold text-emerald-700">present on time</span>.{' '}
                <span className="text-emerald-700 font-semibold">Well done!</span></>
            ) : (
              <>you are <span className="font-bold text-red-700">{minutesLate} minute{minutesLate === 1 ? '' : 's'} late</span> today.</>
            )}
          </p>

          <div
            className="rounded-xl border p-3 flex items-start gap-3"
            style={{ borderColor: accentBorder, background: accentBg }}
          >
            {onTime ? (
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
            ) : (
              <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
                Today
              </div>
              <div className="text-[13px] text-slate-700 leading-snug mt-1">
                Shift starts <b className="font-semibold text-slate-900">{dutyStart}</b>
                {firstLoginDisplay ? (
                  <> · First login <b className="font-semibold text-slate-900">{firstLoginDisplay}</b></>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Total late time · {monthLabel}
            </div>
            <div className="text-[15px] font-extrabold text-slate-900 mt-1 tabular-nums" data-testid="attendance-month-total">
              {monthlyTotalMinutes > 0
                ? formatDuration(monthlyHours, monthlyRemainder)
                : 'On time all month — keep it up!'}
            </div>
          </div>

          <button
            type="button"
            onClick={dismissAttendance}
            className="w-full h-11 rounded-full bg-[#0F172A] hover:bg-[#020617] active:scale-[0.98] text-white text-sm font-bold flex items-center justify-center gap-2 transition"
            data-testid="attendance-dismiss"
          >
            Acknowledge &amp; continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceDialog;
